import {AfterViewInit, Component, OnDestroy, OnInit, TemplateRef, ViewChild} from '@angular/core';
import {CartService} from '../../../services/cart/cart.service';
import {BookingsService} from '../../../services/bookings/bookings.service';
import {StorageService} from '../../../services/storage/storage.service';
import {RouteService} from '../../../services/route/route.service';
import {SnackService} from '../../../services/snack/snack.service';
import {AuthService} from '../../../services/auth/auth.service';
import {FiscalService} from '../../../services/fiscal/fiscal.service';
import {UtilService} from '../../../services/util/util.service';
import {TranslateService} from '@ngx-translate/core';
import {Scrap} from '../../../generated/sales-terminal-client';
import moment from 'moment';
import {Subscription} from 'rxjs';
import {ScrapService} from '../../../services/scrap/scrap.service';
import {finalize} from 'rxjs/operators';
import {ChildSelectionState} from '../../../services/cart/cart-base.service';
import {DataTable, DataTableRowItemType} from '../../../components/data-table/data-table.interfaces';
import {TextInputType} from '../../../components/forms/text-input/text-input.component';

@Component({
  selector: 'app-scrap',
  templateUrl: './scrap.component.html',
  styleUrls: ['./scrap.component.scss']
})
export class ScrapComponent implements OnInit, OnDestroy, AfterViewInit {
  description: string;
  isLoading = false;
  processScrapViewOpened = false;
  private _subscriptions: Subscription[] = [];
  public tableConfig: DataTable;

  @ViewChild('tableEvent')
  tableEvent: TemplateRef<any>;
  @ViewChild('tableDate')
  tableDate: TemplateRef<any>;
  @ViewChild('tableTicketPrice')
  tableTicketPrice: TemplateRef<any>;
  @ViewChild('tableBlankNumber')
  tableBlankNumber: TemplateRef<any>;

  constructor(public scrapService: ScrapService,
              private bookingService: BookingsService,
              private routeService: RouteService,
              private snackService: SnackService,
              private translateService: TranslateService,
              private storageService: StorageService,
              public authService: AuthService,
              private fiscalService: FiscalService,
              public utilService: UtilService) {}

  public ngOnInit(): void {
    this.scrapService.selectAllTickets();
  }
  public ngAfterViewInit(): void {
    this.setupTable();
    this.scrapService.retryCart();
  }
  public ngOnDestroy(): void {
    this._subscriptions.forEach(s => s.unsubscribe());
    this._subscriptions = [];
  }

  public get isFiscal(): boolean {
    return this.authService.checkSalespointProperty('fiscal_procedures_by');
  }

  public cancelScrapping(): void {
    this.snackService.showSuccess(this.translateService.instant('general.api_cart_purchase_succeed'));
    this.clearScrapAndReturnHome();
  }

  public get canSend(): boolean {
    return !!(this.scrapService.hasSelectedTickets && this.description);
  }

  public isTicketSelected(ticketId: number, eventId: number): boolean {
    return this.scrapService.selectionMap.events[eventId].tickets[ticketId] === ChildSelectionState.ALL;
  }

  public registerScrap(): void {
    this.isLoading = true;
    const scrap: Scrap = {
      tickets: [],
      description: this.description
    };
    this.scrapService.events.forEach(event => {
      event.legacyTickets.forEach(ticket => {
        if (this.isTicketSelected(ticket.id, event.id)) {
          scrap.tickets.push({ticketId: ticket.id, blankNumber: ticket.blankNumber});
        }
      })
    });
    if (this.routeService.checkRoute(['sales-terminal', 'booking-scrap']) && !this.authService.checkSalespointProperty('fiscal_procedures_by')) {
      this.bookingService.scrapBooking(scrap, parseFloat(this.routeService.params['id'])).pipe(
        finalize(() => this.isLoading = false)
      ).subscribe(() => {
        this.clearScrapAndReturnHome();
        this.snackService.showSuccess(this.translateService.instant('general.api_scrap_success'));
      });
    } else {
      if (this.authService.checkSalespointProperty('fiscal_procedures_by')) {
        this.fiscalService.isTransaction = true;
        this.fiscalService.scrap(scrap).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe(() => {
          this._subscriptions.push(this.utilService.fiscalOperationFinished.subscribe(() => this.clearScrapAndReturnHome()));
          this._subscriptions.push(this.utilService.fiscalOperationFailed.subscribe(() => this.ngOnDestroy()));
        });
      } else {
        this.scrapService.processScrap(scrap).pipe(
          finalize(() => this.isLoading = false)
        ).subscribe(() => {
          this.clearScrapAndReturnHome();
          this.snackService.showSuccess(this.translateService.instant('general.api_scrap_success'));
        });
      }
    }
  }

  private clearScrapAndReturnHome(): void {
    this.storageService.set(CartService.STORAGE_CART_WINDOW_CLOSE, moment.now());
    this.scrapService.clearScrap();
    this.routeService.navigate('/');
  }

  private setupTable(): void {
    this.tableConfig = {
      header: {
        items: [
          {
            key: 'selected',
            value: false,
            format: 'general.api_cart_table_ticket_id',
            type: DataTableRowItemType.Checkbox,
            cssClass: 'min-width'
          },
          {
            key: 'ticket_id',
            format: 'general.api_cart_table_ticket_id'
          },
          {
            key: 'event',
            format: 'general.api_cart_table_event_title',
            children: [
              {
                key: 'venue',
                format: 'general.api_cart_table_event_venue',
                cssClass: 'subtitle'
              }
            ]
          },
          {
            key: 'date',
            format: 'general.api_cart_table_date'
          },
          {
            key: 'sector',
            format: 'general.api_cart_table_sector',
          },
          {
            key: 'row',
            format: 'general.api_cart_table_row',
          },
          {
            key: 'seat',
            format: 'general.api_cart_table_seat',
          },
          {
            key: 'ticketPrice',
            format: 'general.api_cart_table_price',
            cssClass: 'align-right'
          },
          {
            key: 'blankNumber',
            format: 'general.api_cart_table_blanknumber',
            width: 1
          }
        ]
      },
      body: {
        items: [
          {
            key: 'selected',
            value: 'id',
            type: DataTableRowItemType.Checkbox,
            mobileHeaderItem: true
          },
          {
            key: 'ticket_id',
            format: 'id',
            useModel: true,
            mobileHeaderItem: true
          },
          {
            key: 'event',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableEvent,
            templateMobile: this.tableEvent
          },
          {
            key: 'date',
            useParent: true,
            type: DataTableRowItemType.Template,
            template: this.tableDate,
            templateMobile: this.tableDate,
            cssClass: 'white-space-no-wrap'
          },
          {
            key: 'sector',
            format: 'sectorName',
            useModel: true,
          },
          {
            key: 'row',
            format: 'row',
            useModel: true,
          },
          {
            key: 'place',
            format: 'place',
            useModel: true,
          },
          {
            key: 'ticketPrice',
            type: DataTableRowItemType.Template,
            template: this.tableTicketPrice,
            templateMobile: this.tableTicketPrice,
            cssClass: 'align-right'
          },
          {
            key: 'blankNumber',
            type: DataTableRowItemType.Template,
            template: this.tableBlankNumber,
            templateMobile: this.tableBlankNumber
          }
        ]
      },
      customSelector: 'legacyTickets',
      parentValue: 'id'
    };
  }

  protected readonly Object = Object;
  protected readonly TextInputType = TextInputType;
}
